import { Search } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

const SearchInput = ({
    value,
    onChange,
    placeholder = "Search...",
    disabled = false,
    suggestions = [],
    onSuggestionSelect,
}) => {
    const inputId = useId().replace(/:/g, "");
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const wrapperRef = useRef(null);
    const suppressAutoOpenRef = useRef(false);
    const showSuggestions = !disabled && suggestions.length > 0 && isOpen;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!wrapperRef.current?.contains(event.target)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const suggestionItems = useMemo(() => suggestions.slice(0, 6), [suggestions]);

    useEffect(() => {
        if (!showSuggestions) {
            setHighlightedIndex(-1);
        } else if (highlightedIndex >= suggestions.length) {
            setHighlightedIndex(0);
        }
    }, [showSuggestions, highlightedIndex, suggestions.length]);

    useEffect(() => {
        const isFocusedWithin = wrapperRef.current?.contains(document.activeElement);
        if (isFocusedWithin && suggestionItems.length > 0 && !suppressAutoOpenRef.current) {
            setIsOpen(true);
        } else if (!suggestionItems.length) {
            setIsOpen(false);
        }
    }, [suggestionItems]);

    const selectSuggestion = (suggestion) => {
        suppressAutoOpenRef.current = true;
        if (onSuggestionSelect) {
            onSuggestionSelect(suggestion);
        } else if (onChange) {
            onChange({ target: { value: suggestion } });
        }
        setIsOpen(false);
        setHighlightedIndex(-1);
    };

    return (
    <div ref={wrapperRef} className="group relative w-full max-w-md">
        <input
            type="text"
            tabIndex={-1}
            autoComplete="username"
            className="hidden"
            aria-hidden="true"
        />
        <input
            type="password"
            tabIndex={-1}
            autoComplete="current-password"
            className="hidden"
            aria-hidden="true"
        />
        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-slate-700">
            <Search className="h-4.5 w-4.5" />
        </span>
        <input
            type="search"
            id={`search-${inputId}`}
            value={value}
            onChange={(e) => {
                suppressAutoOpenRef.current = false;
                onChange?.(e);
            }}
            placeholder={placeholder}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            name={`search-${inputId}`}
            disabled={disabled}
            aria-autocomplete="list"
            aria-expanded={showSuggestions}
            aria-controls={`search-suggestions-${inputId}`}
            className="w-full rounded-2xl border border-slate-200 bg-white/90 py-3 pl-11 pr-4 text-sm text-slate-900 shadow-[0_10px_25px_rgba(15,23,42,0.05)] transition-all duration-200 placeholder:text-slate-400 focus:outline-none box-border"
            style={{ color: "var(--color-text)" }}
            onFocus={(e) => {
                if (suggestionItems.length > 0 && !suppressAutoOpenRef.current) {
                    setIsOpen(true);
                }
                e.target.style.borderColor = "var(--color-primary)";
                e.target.style.boxShadow = `0 0 0 4px color-mix(in srgb, var(--color-primary) 14%, white), 0 12px 28px rgba(15,23,42,0.08)`;
            }}
            onBlur={(e) => {
                e.target.style.borderColor = "#e2e8f0";
                e.target.style.boxShadow = "0 10px 25px rgba(15,23,42,0.05)";
            }}
            onKeyDown={(e) => {
                if (!suggestionItems.length) return;

                if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setIsOpen(true);
                    setHighlightedIndex((prev) => (prev + 1) % suggestionItems.length);
                }

                if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setIsOpen(true);
                    setHighlightedIndex((prev) => (prev <= 0 ? suggestionItems.length - 1 : prev - 1));
                }

                if (e.key === "Enter" && isOpen && highlightedIndex >= 0) {
                    e.preventDefault();
                    selectSuggestion(suggestionItems[highlightedIndex]);
                }

                if (e.key === "Escape") {
                    setIsOpen(false);
                    setHighlightedIndex(-1);
                }
            }}
        />
        {showSuggestions && (
            <div
                id={`search-suggestions-${inputId}`}
                className="absolute left-0 right-0 top-[calc(100%+10px)] z-20 overflow-hidden rounded-2xl border border-slate-200 bg-white/95 shadow-[0_18px_45px_rgba(15,23,42,0.14)] backdrop-blur"
                role="listbox"
            >
                {suggestionItems.map((suggestion, index) => {
                    const isHighlighted = index === highlightedIndex;
                    return (
                        <button
                            key={`${suggestion}-${index}`}
                            type="button"
                            role="option"
                            aria-selected={isHighlighted}
                            className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition ${
                                isHighlighted ? "bg-slate-100 text-slate-900" : "bg-transparent text-slate-600 hover:bg-slate-50"
                            }`}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                selectSuggestion(suggestion);
                            }}
                            onMouseEnter={() => setHighlightedIndex(index)}
                        >
                            <Search className="h-4 w-4 text-slate-400" />
                            <span className="truncate">{suggestion}</span>
                        </button>
                    );
                })}
            </div>
        )}
    </div>
    );
};

export default SearchInput;
